import { useState, useEffect } from "react";
import { Loader2, Coins } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { useTenant } from "../../contexts/TenantContext";
import { updateSystemSettings } from "../../services/api";
import { CURRENCY_OPTIONS } from "../../utils/currency";

export default function CurrencySection() {
  const tenant = useTenant();
  const [selected, setSelected] = useState(tenant.currency?.code || "USD");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenant.currency?.code) setSelected(tenant.currency.code);
  }, [tenant.currency?.code]);

  const handleChange = async (e) => {
    const code = e.target.value;
    setSelected(code);
    setError(null);
    setSuccess(null);

    const option = CURRENCY_OPTIONS.find((o) => o.code === code);
    if (!option) return;

    setSaving(true);
    try {
      await updateSystemSettings({
        currency: {
          code: option.code,
          symbol: option.symbol,
          locale: option.locale,
        },
      });
      tenant.reloadSettings();
      setSuccess("Currency updated");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Coins size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          Currency
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Set the display currency for all pipeline values, deal amounts, and
        revenue figures across Insights.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <select
          value={selected}
          onChange={handleChange}
          disabled={saving}
          style={{
            padding: "9px 12px",
            fontSize: 13,
            fontFamily: fonts.body,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.bg,
            color: colors.text,
            outline: "none",
            cursor: "pointer",
            minWidth: 160,
          }}
        >
          {CURRENCY_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
        {saving && (
          <Loader2
            size={16}
            color={colors.textSecondary}
            className="spin"
          />
        )}
      </div>
    </div>
  );
}
